import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { ApplicationFeature } from '@app/core/models/application-feature-enums';
import { UserFeaturesService } from '@app/core/services/user-features.service';
import { combineLatest } from 'rxjs';
import { take } from 'rxjs/operators';
import { MkoPermissions, UserInfoService, YourComplianceTrainingPermissions } from './user-info.service';

/**
 * Your Compliance Training (the Platform version of MKO's MyTopics2 page) is released behind the YourComplianceTraining app feature.
 * The user's location needs both MKO and YourComplianceTraining, and the user needs both access permissions.
 */
@Injectable({ providedIn: 'root' })
export class YourComplianceTrainingAuthGuardService implements CanActivate {
	constructor(
		private userFeaturesService: UserFeaturesService,
		private userinfoService: UserInfoService,
		private router: Router
	) {}

	canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
		return new Promise((resolve) => {
			// Any failure while checking access denies it, so navigation never hangs on an unresolved promise
			const deny = (): void => {
				this.router.navigate(['/landing']);
				resolve(false);
			};

			this.userFeaturesService.getMyFeatures().subscribe({
				next: (result) => {
					let hasFeatures = false;

					if (result && result.result) {
						const features = result.result.applicationFeatures ?? [];
						const hasMko = features.includes(ApplicationFeature[ApplicationFeature.MkoApplication]);
						const hasYourComplianceTraining = features.includes(ApplicationFeature[ApplicationFeature.YourComplianceTraining]);
						hasFeatures = hasMko && hasYourComplianceTraining;
					}

					combineLatest([
						this.userinfoService.hasPermission(ApplicationFeature.MkoApplication, MkoPermissions.ApplicationMembership),
						this.userinfoService.hasPermission(
							ApplicationFeature.YourComplianceTraining,
							YourComplianceTrainingPermissions.AccessYourComplianceTraining
						),
					])
						.pipe(take(1))
						.subscribe({
							next: ([hasMkoPermission, hasYourComplianceTrainingPermission]) => {
								const isAllowed = hasFeatures && hasMkoPermission && hasYourComplianceTrainingPermission;

								if (!isAllowed) {
									deny();
									return;
								}

								resolve(true);
							},
							error: () => deny(),
						});
				},
				error: () => deny(),
			});
		});
	}
}
